/**
 * Audio Manager - Realistic Space Ambiance
 * 
 * Creates immersive space sounds using procedural audio:
 * - Solar wind (filtered noise)
 * - Pulsar pings (distant radar-like)
 * - Radio interference sweeps
 * - Deep cosmic rumble
 */

import Phaser from 'phaser';

type SFXType = 'launch' | 'land' | 'death' | 'powerup' | 'combo' | 'ui' | 'falling';

export class AudioManager {
  private static instances: Map<Phaser.Scene, AudioManager> = new Map();
  private scene: Phaser.Scene;
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxEnabled = true;
  private musicEnabled = true;
  
  // Space ambient sounds
  private musicPlaying = false;
  private padOscillators: OscillatorNode[] = [];
  private padGains: GainNode[] = [];
  private solarWindSource: AudioBufferSourceNode | null = null;
  private spaceInterval1: number | null = null;
  private spaceInterval2: number | null = null;
  private spaceInterval3: number | null = null;
  
  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.init();
  }
  
  static getInstance(scene: Phaser.Scene): AudioManager {
    if (!AudioManager.instances.has(scene)) {
      AudioManager.instances.set(scene, new AudioManager(scene));
    }
    return AudioManager.instances.get(scene)!;
  }
  
  private init(): void {
    try {
      const existingContext = this.scene.registry.get('audioContext');
      if (existingContext) {
        this.audioContext = existingContext;
      } else {
        const AudioContextClass = window.AudioContext || 
          (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioContextClass) {
          this.audioContext = new AudioContextClass();
          this.scene.registry.set('audioContext', this.audioContext);
        }
      }
      
      if (this.audioContext) {
        this.masterGain = this.audioContext.createGain();
        this.masterGain.connect(this.audioContext.destination);
        this.masterGain.gain.value = 0.5;
        
        this.musicGain = this.audioContext.createGain();
        this.musicGain.connect(this.masterGain);
        this.musicGain.gain.value = 0.8; // 80% volume
      }
      
      this.sfxEnabled = this.scene.registry.get('soundEnabled') !== false;
      this.musicEnabled = this.scene.registry.get('musicEnabled') !== false;
    } catch (error) {
      console.warn('[AudioManager] Init failed:', error);
    }
  }
  
  // =========== REALISTIC SPACE AMBIENT ===========
  
  startMusic(): void {
    if (!this.musicEnabled || !this.audioContext || !this.musicGain || this.musicPlaying) return;
    
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    
    this.musicPlaying = true;
    console.log('[AudioManager] Starting space ambiance');
    
    // Deep cosmic drone (like distant stars humming)
    this.createCosmicDrone();
    
    // Solar wind - continuous filtered noise
    this.startSolarWind();
    
    // Pulsar pings - distant radar-like sounds
    this.spaceInterval1 = window.setInterval(() => {
      if (Math.random() > 0.5) this.playPulsarPing();
    }, 3000);
    
    // Radio interference sweeps
    this.spaceInterval2 = window.setInterval(() => {
      if (Math.random() > 0.6) this.playRadioSweep();
    }, 5000);
    
    // Distant cosmic whispers
    this.spaceInterval3 = window.setInterval(() => {
      if (Math.random() > 0.7) this.playCosmicWhisper();
    }, 7000);
  }
  
  stopMusic(): void {
    if (!this.musicPlaying) return;
    this.musicPlaying = false;
    
    // Fade out pads
    this.padGains.forEach(gain => {
      if (gain && this.audioContext) {
        gain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.5);
      }
    });
    
    setTimeout(() => {
      this.padOscillators.forEach(osc => {
        try { osc.stop(); } catch {}
      });
      this.padOscillators = [];
      this.padGains = [];
    }, 600);
    
    // Stop solar wind
    if (this.solarWindSource) {
      try { this.solarWindSource.stop(); } catch {}
      this.solarWindSource = null;
    }
    
    // Clear intervals
    if (this.spaceInterval1) { clearInterval(this.spaceInterval1); this.spaceInterval1 = null; }
    if (this.spaceInterval2) { clearInterval(this.spaceInterval2); this.spaceInterval2 = null; }
    if (this.spaceInterval3) { clearInterval(this.spaceInterval3); this.spaceInterval3 = null; }
  }
  
  /**
   * Deep cosmic drone - like the universe humming
   */
  private createCosmicDrone(): void {
    if (!this.audioContext || !this.musicGain) return;
    
    // Layer of deep frequencies
    [35, 55, 82].forEach((freq, i) => {
      const osc = this.audioContext!.createOscillator();
      const gain = this.audioContext!.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      // Slow drift
      const lfo = this.audioContext!.createOscillator();
      const lfoGain = this.audioContext!.createGain();
      lfo.type = 'sine';
      lfo.frequency.value = 0.03 + i * 0.01;
      lfoGain.gain.value = freq * 0.015;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      lfo.start();
      
      gain.gain.value = 0.08 - i * 0.02;
      
      const filter = this.audioContext!.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 200;
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.musicGain!);
      
      osc.start();
      this.padOscillators.push(osc);
      this.padGains.push(gain);
    });
  }
  
  /**
   * Solar wind - continuous filtered noise (like actual NASA recordings)
   */
  private startSolarWind(): void {
    if (!this.audioContext || !this.musicGain) return;
    
    const bufferSize = this.audioContext.sampleRate * 4; // 4 second loop
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Create wind-like noise with slow variations
    for (let i = 0; i < bufferSize; i++) {
      const t = i / this.audioContext.sampleRate;
      const envelope = 0.5 + 0.5 * Math.sin(t * 0.3); // Slow breathing
      data[i] = (Math.random() * 2 - 1) * 0.3 * envelope;
    }
    
    this.solarWindSource = this.audioContext.createBufferSource();
    this.solarWindSource.buffer = buffer;
    this.solarWindSource.loop = true;
    
    // Heavy filtering for that distant wind sound
    const filter1 = this.audioContext.createBiquadFilter();
    filter1.type = 'bandpass';
    filter1.frequency.value = 300;
    filter1.Q.value = 0.5;
    
    const filter2 = this.audioContext.createBiquadFilter();
    filter2.type = 'lowpass';
    filter2.frequency.value = 600;
    
    const gain = this.audioContext.createGain();
    gain.gain.value = 0.08;
    
    this.solarWindSource.connect(filter1);
    filter1.connect(filter2);
    filter2.connect(gain);
    gain.connect(this.musicGain);
    
    this.solarWindSource.start();
  }
  
  /**
   * Pulsar ping - distant radar/beacon sound
   */
  private playPulsarPing(): void {
    if (!this.audioContext || !this.musicGain) return;
    
    const now = this.audioContext.currentTime;
    const freq = 800 + Math.random() * 400; // Random high frequency
    
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = freq;
    
    // Quick ping with echo feel
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.06, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    
    osc.connect(gain);
    gain.connect(this.musicGain);
    
    osc.start(now);
    osc.stop(now + 0.8);
    
    // Echo ping (quieter, delayed)
    setTimeout(() => {
      if (!this.audioContext || !this.musicGain) return;
      const echoNow = this.audioContext.currentTime;
      const echoOsc = this.audioContext.createOscillator();
      const echoGain = this.audioContext.createGain();
      
      echoOsc.type = 'sine';
      echoOsc.frequency.value = freq * 0.99; // Slightly detuned
      
      echoGain.gain.setValueAtTime(0.02, echoNow);
      echoGain.gain.exponentialRampToValueAtTime(0.001, echoNow + 0.5);
      
      echoOsc.connect(echoGain);
      echoGain.connect(this.musicGain);
      
      echoOsc.start(echoNow);
      echoOsc.stop(echoNow + 0.5);
    }, 150);
  }
  
  /**
   * Radio interference sweep - like tuning through frequencies
   */
  private playRadioSweep(): void {
    if (!this.audioContext || !this.musicGain) return;
    
    const now = this.audioContext.currentTime;
    
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sawtooth';
    
    // Frequency sweep
    const startFreq = 100 + Math.random() * 200;
    const endFreq = 400 + Math.random() * 300;
    
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.exponentialRampToValueAtTime(endFreq, now + 1.5);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.02, now + 0.2);
    gain.gain.linearRampToValueAtTime(0.015, now + 1);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
    
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 400;
    filter.Q.value = 2;
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicGain);
    
    osc.start(now);
    osc.stop(now + 1.5);
  }
  
  /**
   * Cosmic whisper - ethereal tone fading in/out
   */
  private playCosmicWhisper(): void {
    if (!this.audioContext || !this.musicGain) return;
    
    const now = this.audioContext.currentTime;
    const notes = [220, 277, 330, 440, 554];
    const freq = notes[Math.floor(Math.random() * notes.length)];
    
    const osc1 = this.audioContext.createOscillator();
    const osc2 = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = freq;
    osc2.frequency.value = freq * 1.003; // Slight detune for shimmer
    
    // Slow fade in and out
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.04, now + 1);
    gain.gain.linearRampToValueAtTime(0, now + 3);
    
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1000;
    
    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicGain);
    
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 3);
    osc2.stop(now + 3);
  }
  
  // =========== SOUND EFFECTS ===========
  
  playSFX(type: SFXType): void {
    if (!this.sfxEnabled || !this.audioContext || !this.masterGain) return;
    
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    
    try {
      switch (type) {
        case 'launch': this.playLaunchSound(); break;
        case 'land': this.playLandSound(); break;
        case 'death': this.playDeathSound(); break;
        case 'powerup': this.playPowerupSound(); break;
        case 'combo': this.playComboSound(); break;
        case 'ui': this.playUISound(); break;
        case 'falling': this.playFallingSound(); break;
      }
    } catch (error) {
      console.warn(`[AudioManager] SFX error:`, error);
    }
  }
  
  private playLaunchSound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    
    // Soft whoosh
    const bufferSize = this.audioContext.sampleRate * 0.2;
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      const env = Math.sin((i / bufferSize) * Math.PI);
      data[i] = (Math.random() * 2 - 1) * env * 0.5;
    }
    
    const noise = this.audioContext.createBufferSource();
    noise.buffer = buffer;
    
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, now);
    filter.frequency.linearRampToValueAtTime(2000, now + 0.1);
    filter.frequency.linearRampToValueAtTime(600, now + 0.2);
    filter.Q.value = 1;
    
    const gain = this.audioContext.createGain();
    gain.gain.value = 0.15;
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    
    noise.start(now);
  }
  
  private playLandSound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(100, now);
    osc.frequency.exponentialRampToValueAtTime(40, now + 0.15);
    
    gain.gain.setValueAtTime(0.25, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 150;
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    
    osc.start(now);
    osc.stop(now + 0.25);
  }
  
  private playFallingSound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(350, now);
    osc.frequency.exponentialRampToValueAtTime(100, now + 0.25);
    
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    
    osc.connect(gain);
    gain.connect(this.masterGain);
    
    osc.start(now);
    osc.stop(now + 0.25);
  }
  
  private playDeathSound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    
    const osc1 = this.audioContext.createOscillator();
    const osc2 = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = 80;
    osc2.frequency.value = 120;
    
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
    
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    
    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.7);
    osc2.stop(now + 0.7);
  }
  
  private playComboSound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = 880;
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.1, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
    
    osc.connect(gain);
    gain.connect(this.masterGain);
    
    osc.start(now);
    osc.stop(now + 0.4);
  }
  
  private playPowerupSound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    [523, 659, 784].forEach((freq, i) => {
      const osc = this.audioContext!.createOscillator();
      const gain = this.audioContext!.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      const t = now + i * 0.08;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.08, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
      
      osc.connect(gain);
      gain.connect(this.masterGain!);
      
      osc.start(t);
      osc.stop(t + 0.25);
    });
  }
  
  private playUISound(): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = 600;
    
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
    
    osc.connect(gain);
    gain.connect(this.masterGain);
    
    osc.start(now);
    osc.stop(now + 0.06);
  }
  
  // Settings
  setSFXEnabled(enabled: boolean): void {
    this.sfxEnabled = enabled;
    this.scene.registry.set('soundEnabled', enabled);
  }
  
  setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    this.scene.registry.set('musicEnabled', enabled);
    if (!enabled) this.stopMusic();
  }
  
  isSFXEnabled(): boolean { return this.sfxEnabled; }
  isMusicEnabled(): boolean { return this.musicEnabled; }
  
  setVolume(volume: number): void {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }
  
  destroy(): void {
    this.stopMusic();
    AudioManager.instances.delete(this.scene);
  }
}

export default AudioManager;
